import React, { useCallback, useId, useRef, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { KIT_FONT_IDS, KitFontId, RELEASE_THEMES, ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../../common/types';
import { releaseThemeMessages } from '../../themes/releaseThemeMessages';
import { kitFontName, kitFontStyle } from '../../themes/kitWebFonts';
import { themeMessages } from './themeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Theme panel; each part uses its own slice of it */
import styles from './ThemePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ThemeFontRowProps {
  themeId: ReleaseThemeId;
  fontOverride: KitFontId | null;
  disabled: boolean;
  /** Null: the font of the theme. */
  onChange: (fontOverride: KitFontId | null) => void;
}

/**
 * "Title font" (design q2, q2b): the display font of the look, in its own face. "Change font" is
 * closed by default; open, it lists the 9 kit fonts, each in its own face, and marks the font of
 * the theme. A pick of the theme font clears the override. A theme change keeps an override.
 */
const ThemeFontRowComponent: React.FC<ThemeFontRowProps> = ({ themeId, fontOverride, disabled, onChange }) => {
  const intl = useIntl();
  const gridId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const chips = useRef<Array<HTMLButtonElement | null>>([]);
  const themeFont = RELEASE_THEMES[themeId].type.display;
  const current = fontOverride ?? themeFont;
  const isChanged = fontOverride !== null && fontOverride !== themeFont;
  const themeName = intl.formatMessage(releaseThemeMessages[themeId].name);

  const toggle = useCallback(() => setIsOpen((open) => !open), []);
  const reset = useCallback(() => onChange(null), [onChange]);
  const pick = useCallback(
    (fontId: KitFontId) => onChange(fontId === themeFont ? null : fontId),
    [onChange, themeFont]
  );

  const onKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    const step = { ArrowRight: 1, ArrowDown: 3, ArrowLeft: -1, ArrowUp: -3 }[event.key];
    if (step === undefined) {
      return;
    }
    event.preventDefault();
    const index = chips.current.findIndex((chip) => chip === document.activeElement);
    const next = (Math.max(0, index) + step + KIT_FONT_IDS.length) % KIT_FONT_IDS.length;
    chips.current[next]?.focus();
  }, []);

  return (
    <>
      <div className={styles.row}>
        <span className={styles.aa} style={kitFontStyle(current)} aria-hidden="true">
          Aa
        </span>
        <div className={styles.grow}>
          <b className={styles.rowTitle}>
            {intl.formatMessage(themeMessages.fontTitle, { font: kitFontName(current) })}
          </b>
          <small className={styles.rowHint}>
            {isChanged
              ? intl.formatMessage(themeMessages.fontChosen, { theme: themeName, font: kitFontName(themeFont) })
              : intl.formatMessage(themeMessages.fontOfTheme, { theme: themeName })}
          </small>
        </div>
        {isChanged && (
          <button type="button" className={styles.linkButton} disabled={disabled} onClick={reset}>
            {intl.formatMessage(themeMessages.fontReset)}
          </button>
        )}
        <button
          type="button"
          className={styles.smallButton}
          aria-expanded={isOpen}
          aria-controls={gridId}
          onClick={toggle}
        >
          {intl.formatMessage(isOpen ? themeMessages.fontClose : themeMessages.fontOpen)}
        </button>
      </div>
      {isOpen && (
        <div
          id={gridId}
          className={styles.fontGrid}
          role="radiogroup"
          // The radio buttons take the focus; the group only takes the arrow keys.
          tabIndex={-1}
          aria-label={intl.formatMessage(themeMessages.fontGroup)}
        >
          {KIT_FONT_IDS.map((fontId, index) => {
            const isOn = fontId === current;
            return (
              <button
                key={fontId}
                ref={(chip) => {
                  chips.current[index] = chip;
                }}
                type="button"
                role="radio"
                aria-checked={isOn}
                tabIndex={isOn ? 0 : -1}
                disabled={disabled}
                className={clsx(styles.fontChip, isOn && styles.fontChipOn)}
                data-font={fontId}
                onClick={() => pick(fontId)}
                onKeyDown={onKeyDown}
              >
                <span className={styles.fontChipAa} style={kitFontStyle(fontId)} aria-hidden="true">
                  Aa
                </span>
                <span className={styles.fontChipName}>{kitFontName(fontId)}</span>
                {fontId === themeFont && (
                  <span className={styles.fontChipOwn}>{intl.formatMessage(themeMessages.fontThemeBadge)}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </>
  );
};

export const ThemeFontRow = TypedMemo(ThemeFontRowComponent);
